import { useEffect, useMemo } from "react";
import { localeToOpenGraphLocale } from "../../intl/manifestI18n";
import { useOptionalClientView } from "../context/ClientViewProvider";
import { formatClientViewPageTitle } from "../../seo/clientViewBrandSeo";
import { applyDocumentTitle, upsertMeta as upsertHeadMeta } from "../../seo/documentHead";

function upsertMeta(attr, name, content) {
  upsertHeadMeta(attr, name, content);
}

function upsertCanonical(href) {
  if (!href) return;
  let element = document.querySelector('link[rel="canonical"]');
  if (!element) {
    element = document.createElement("link");
    element.setAttribute("rel", "canonical");
    document.head.appendChild(element);
  }
  element.setAttribute("href", href);
}

function upsertAlternateLinks(alternates = []) {
  document.querySelectorAll('link[rel="alternate"][data-client-view-locale="true"]').forEach((node) => {
    node.remove();
  });
  alternates.forEach(({ hreflang, href }) => {
    if (!href || !hreflang) return;
    const link = document.createElement("link");
    link.setAttribute("rel", "alternate");
    link.setAttribute("hreflang", hreflang);
    link.setAttribute("href", href);
    link.setAttribute("data-client-view-locale", "true");
    document.head.appendChild(link);
  });
}

function upsertJsonLd(id, data) {
  const existing = document.getElementById(id);
  if (existing) {
    existing.remove();
  }
  if (!data || (Array.isArray(data) && !data.length)) {
    return;
  }
  const script = document.createElement("script");
  script.id = id;
  script.type = "application/ld+json";
  script.textContent = JSON.stringify(Array.isArray(data) && data.length === 1 ? data[0] : data);
  document.head.appendChild(script);
}

function applyRobots(robotsDirective) {
  if (!robotsDirective) return;
  upsertMeta("name", "robots", robotsDirective);
}

function applyOpenGraph(og = {}) {
  upsertMeta("property", "og:title", og.title);
  upsertMeta("property", "og:description", og.description);
  upsertMeta("property", "og:image", og.image);
  upsertMeta("property", "og:url", og.url);
  upsertMeta("property", "og:type", og.type);
  upsertMeta("property", "og:site_name", og.siteName);
  upsertMeta("property", "og:locale", og.locale);
}

function applyTwitter(twitter = {}) {
  upsertMeta("name", "twitter:card", twitter.card);
  upsertMeta("name", "twitter:title", twitter.title);
  upsertMeta("name", "twitter:description", twitter.description);
  upsertMeta("name", "twitter:image", twitter.image);
}

/**
 * Apply SEO metadata to document head (client-side; see docs for SSR limits).
 */
export function useClientViewSeo({
  title,
  description,
  canonicalUrl,
  robots,
  openGraph,
  twitter,
  structuredData,
  jsonLdId = "client-view-jsonld",
  alternateLocales = [],
  locale = null,
  brandSuffix = "auto",
}) {
  const clientView = useOptionalClientView();
  const resolvedTitle = useMemo(() => {
    if (!title) {
      return null;
    }
    if (brandSuffix === false) {
      return title;
    }
    const manifestBrand = clientView?.manifest?.branding?.name;
    const brandName =
      brandSuffix === "auto" || brandSuffix == null
        ? manifestBrand
        : typeof brandSuffix === "string"
          ? brandSuffix
          : manifestBrand;
    if (!brandName) {
      return title;
    }
    return formatClientViewPageTitle(title, brandName);
  }, [title, brandSuffix, clientView?.manifest?.branding?.name]);

  useEffect(() => {
    const previousTitle = typeof document !== "undefined" ? document.title : "";

    if (resolvedTitle) {
      applyDocumentTitle(resolvedTitle);
    }
    upsertMeta("name", "description", description);
    applyRobots(robots);
    upsertCanonical(canonicalUrl);
    upsertAlternateLinks(alternateLocales);
    applyOpenGraph({
      ...openGraph,
      title: openGraph?.title || resolvedTitle || undefined,
      locale: openGraph?.locale || (locale ? localeToOpenGraphLocale(locale) : undefined),
    });
    applyTwitter({
      ...twitter,
      title: twitter?.title || resolvedTitle || undefined,
    });
    upsertJsonLd(jsonLdId, structuredData);

    return () => {
      if (resolvedTitle) {
        applyDocumentTitle(previousTitle);
      }
      const script = document.getElementById(jsonLdId);
      if (script) script.remove();
      document.querySelectorAll('link[rel="alternate"][data-client-view-locale="true"]').forEach((node) => {
        node.remove();
      });
    };
  }, [
    resolvedTitle,
    description,
    canonicalUrl,
    robots,
    openGraph,
    twitter,
    structuredData,
    jsonLdId,
    alternateLocales,
    locale,
  ]);
}

/**
 * Apply a resolved metadata bundle from the public SEO API.
 */
export function useClientViewSeoBundle(metadata, structuredData) {
  useClientViewSeo({
    title: metadata?.metaTitle,
    description: metadata?.metaDescription,
    canonicalUrl: metadata?.canonicalUrl,
    robots: metadata?.robots || metadata?.robotsDirective,
    openGraph: metadata?.openGraph,
    twitter: metadata?.twitter,
    structuredData,
    alternateLocales: metadata?.alternateLocales || [],
    locale: metadata?.openGraph?.locale,
    brandSuffix: false,
  });
}
